import { createStore } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import { MAX_TABS } from '../types';

import type { StateCreator, StoreApi } from 'zustand';
import type {
  PanelComponent,
  ResourceItem,
  SplitDetailPanelStore,
  TabItem,
  TabKey,
} from '../types';

export const STORE_NAME = 'DETAIL_PANEL';
export const KEYS = {
  OPEN_TAB: `${STORE_NAME}/openTab`,
  CLOSE_TAB: `${STORE_NAME}/closeTab`,
  CLOSE_ALL_TABS: `${STORE_NAME}/closeAllTabs`,
  SWITCH_TAB: `${STORE_NAME}/switchTab`,
  RECONCILE_TABS: `${STORE_NAME}/reconcileTabs`,
  SET_VISIBLE: `${STORE_NAME}/setVisible`,
  RESET: `${STORE_NAME}/reset`,
};

// Helper function to create tab key
export function createTabKey(resourceType: string, resourceId: string): TabKey {
  return `${resourceType}:${resourceId}`;
}

// Helper function to parse tab key
export function parseTabKey(key: TabKey): { resourceId: string; resourceType: string } {
  const [resourceType, resourceId] = key.split(':');
  return { resourceType, resourceId };
}

function createSplitDetailPanelSlice<T extends ResourceItem>(
  maxTabLimit: number,
): StateCreator<SplitDetailPanelStore<T>, [['zustand/devtools', never]], []> {
  const initialState = {
    tabs: [] as TabItem<T>[],
    activeTabKey: null as TabKey | null,
    visible: false,
  };

  return (set, get) => ({
    ...initialState,

    openTab: (resourceType, item, component, activate = true, label?: string) => {
      const { tabs, activeTabKey } = get();
      const tabKey = createTabKey(resourceType, item.id);

      // Check if tab already exists
      const existingTabIndex = tabs.findIndex(tab => tab.key === tabKey);

      if (existingTabIndex >= 0) {
        // Tab exists - update item data and optionally activate
        const updatedTabs = tabs.map((tab, index) =>
          index === existingTabIndex
            ? {
                ...tab,
                item,
                component: component as PanelComponent<ResourceItem>,
                label: label ?? tab.label,
              }
            : tab,
        );

        set(
          state => ({
            ...state,
            tabs: updatedTabs as TabItem<T>[],
            activeTabKey: activate ? tabKey : activeTabKey,
            visible: activate ? true : state.visible,
          }),
          undefined,
          KEYS.OPEN_TAB,
        );
        return;
      }

      // Create new tab
      const newTab: TabItem<T> = {
        key: tabKey,
        resourceType,
        resourceId: item.id,
        item,
        component,
        label,
      };

      let newTabs: TabItem<T>[];

      if (tabs.length >= maxTabLimit) {
        // Remove oldest tab (first in array) to make room - FIFO
        newTabs = [...tabs.slice(1), newTab];
      } else {
        newTabs = [...tabs, newTab];
      }

      set(
        state => ({
          ...state,
          tabs: newTabs,
          activeTabKey: activate ? tabKey : activeTabKey,
          visible: activate ? true : state.visible,
        }),
        undefined,
        KEYS.OPEN_TAB,
      );
    },

    closeTab: key => {
      const { tabs, activeTabKey } = get();
      const tabIndex = tabs.findIndex(tab => tab.key === key);

      if (tabIndex < 0) {
        return;
      }

      const newTabs = tabs.filter(tab => tab.key !== key);

      // Determine new active tab if closing active tab
      let newActiveTabKey = activeTabKey;
      if (activeTabKey === key) {
        if (newTabs.length === 0) {
          newActiveTabKey = null;
        } else if (tabIndex >= newTabs.length) {
          // Closed last tab, activate previous
          newActiveTabKey = newTabs[newTabs.length - 1].key;
        } else {
          // Activate tab at same position
          newActiveTabKey = newTabs[tabIndex].key;
        }
      }

      set(
        state => ({
          ...state,
          tabs: newTabs,
          activeTabKey: newActiveTabKey,
          visible: newTabs.length > 0,
        }),
        undefined,
        KEYS.CLOSE_TAB,
      );
    },

    closeAllTabs: resourceType => {
      const { tabs } = get();

      // If resourceType is specified, only close tabs of that type
      const newTabs = resourceType ? tabs.filter(tab => tab.resourceType !== resourceType) : [];

      // No change if same
      if (newTabs.length === tabs.length) {
        return;
      }

      set(
        state => ({
          ...state,
          tabs: newTabs,
          activeTabKey: newTabs.length > 0 ? newTabs[newTabs.length - 1].key : null,
          visible: newTabs.length > 0,
        }),
        undefined,
        KEYS.CLOSE_ALL_TABS,
      );
    },

    switchTab: key => {
      const { tabs } = get();
      const tabExists = tabs.some(tab => tab.key === key);

      if (!tabExists) {
        return;
      }

      set(
        state => ({
          ...state,
          activeTabKey: key,
          visible: true,
        }),
        undefined,
        KEYS.SWITCH_TAB,
      );
    },

    reconcileTabs: availableIdsByType => {
      const { tabs, activeTabKey } = get();

      // Filter tabs based on resourceType and available IDs
      const reconciledTabs = tabs.filter(tab => {
        const availableIds = availableIdsByType[tab.resourceType];

        // If resourceType is not in the reconcile map, keep the tab
        // This allows partial reconciliation (e.g., only update subnets)
        if (!availableIds) {
          return true;
        }

        // Check if this tab's resourceId is in the available list
        return availableIds.includes(tab.resourceId);
      });

      // Check if any tabs were removed
      if (reconciledTabs.length === tabs.length) {
        return;
      }

      // Determine new active tab if current active was removed
      let newActiveTabKey = activeTabKey;
      if (activeTabKey && !reconciledTabs.some(tab => tab.key === activeTabKey)) {
        newActiveTabKey =
          reconciledTabs.length > 0 ? reconciledTabs[reconciledTabs.length - 1].key : null;
      }

      set(
        state => ({
          ...state,
          tabs: reconciledTabs,
          activeTabKey: newActiveTabKey,
          visible: reconciledTabs.length > 0,
        }),
        undefined,
        KEYS.RECONCILE_TABS,
      );
    },

    setVisible: visible => {
      set(state => ({ ...state, visible }), undefined, KEYS.SET_VISIBLE);
    },

    reset: () => {
      set(
        {
          tabs: [] as TabItem<T>[],
          activeTabKey: null,
          visible: false,
        },
        undefined,
        KEYS.RESET,
      );
    },
  });
}

/**
 * Detail Panel Store Factory
 *
 * Creates a zustand store instance for managing tabbed detail panels.
 * Uses subscribeWithSelector middleware for granular subscriptions.
 *
 * Store State:
 * - tabs: Array of open tabs (configurable max limit)
 * - activeTabKey: Currently active tab key (format: "resourceType:id")
 * - visible: Panel visibility state
 *
 * Actions:
 * - openTab: Opens a new tab or activates existing one
 * - closeTab: Closes a tab and selects adjacent tab
 * - closeAllTabs: Closes all tabs (optionally filtered by resourceType)
 * - switchTab: Activates a specific tab
 * - reconcileTabs: Removes tabs for deleted items
 * - setVisible: Controls panel visibility
 * - reset: Resets to initial state
 *
 * @param maxTabs - Maximum number of open tabs (default: MAX_TABS = 5)
 * @param pageKey - Unique identifier for devtools (optional)
 * @returns StoreApi instance (not hook-based)
 *
 * @example
 * ```tsx
 * // Create store instance
 * const store = createSplitDetailPanelStore({ maxTabs: 5, pageKey: 'dns-zones' });
 *
 * // Use in component
 * const tabs = useStore(store, state => state.tabs);
 * ```
 */
export function createSplitDetailPanelStore<T extends ResourceItem = ResourceItem>(
  maxTabs: number = MAX_TABS,
  pageKey?: string,
): StoreApi<SplitDetailPanelStore<T>> {
  return createStore<SplitDetailPanelStore<T>>()(
    subscribeWithSelector(
      devtools(
        (...args) => ({
          ...createSplitDetailPanelSlice<T>(maxTabs)(...args),
        }),
        {
          name: pageKey ? `${STORE_NAME}/${pageKey}` : STORE_NAME,
        },
      ),
    ),
  );
}
